import React from 'react';
import { Typography } from '@material-ui/core';
import Descriptions from 'antd/es/descriptions';
import { OneEquipment } from '../../models/data/Equipment';
import { AuthenticatedContextType } from '../../context/AuthenticatedContext';
import { DeviceDetail } from '../../models/data/Device';
import useStyles, {
  blank,
  contentHeight100,
  contentStyle,
  dataDispWidth,
  labelStyle,
  userContentStyle,
  userLabelStyle,
} from '../../css/style';
import { dateformat } from '../date-range';

interface DataProps {
  userdata: AuthenticatedContextType;
  data: DeviceDetail | undefined;
  data2: OneEquipment | undefined;
}

const DataDisp: React.FC<DataProps> = ({ userdata, data, data2 }) => {
  const classes = useStyles();
  const customerName = userdata.loginCustomer?.customerName ?? '';
  const corporateCode = userdata.loginCustomer?.corporateCode ?? '';
  const numbers = Array.from({ length: 12 }, (_, i) => i + 1); // 基本項目用

  // Itemコンポーネントの定義
  interface ItemProps {
    content1: string | number; // 項目内容
    content2: string | number; // 項目内容
  }

  const ItemDivision: React.FC<ItemProps> = ({ content1, content2 }) => {
    return (
      <Typography className={classes.descriptionItem}>
        <Typography className={classes.description7}>{content1}</Typography>
        <Typography className={classes.description3}>{content2}</Typography>
      </Typography>
    );
  };

  return (
    <div>
      {/* ユーザ情報 */}
      <Descriptions
        bordered
        column={1}
        labelStyle={userLabelStyle}
        contentStyle={userContentStyle}
        style={{ marginBottom: '20px', width: dataDispWidth.userInfo }}
      >
        <Descriptions.Item label="ユーザ名" span={1}>
          {`${corporateCode} ◇ ${customerName}`}
        </Descriptions.Item>
      </Descriptions>
      {/* 関連情報 */}
      <Descriptions
        bordered
        column={2}
        labelStyle={labelStyle}
        contentStyle={contentStyle}
        style={{ marginBottom: '20px', width: dataDispWidth.deviceDetail900 }}
      >
        <Descriptions.Item label="顧客名" span={1}>
          <ItemDivision
            content1={data2?.custName ?? ''}
            content2={data2?.corporateCode ?? ''}
          />
        </Descriptions.Item>
        <Descriptions.Item label="事業所名" span={1}>
          <ItemDivision
            content1={data2?.officeName ?? ''}
            content2={data2?.siteCode ?? ''}
          />
        </Descriptions.Item>
        <Descriptions.Item label="機器名" span={1}>
          <ItemDivision
            content1={data?.deviceName ?? ''}
            content2={data?.mstId ?? ''}
          />
        </Descriptions.Item>
        <Descriptions.Item label="設備名" span={1}>
          <ItemDivision
            content1={data2?.equipmentName ?? ''}
            content2={data2?.equipmentId ?? ''}
          />
        </Descriptions.Item>
      </Descriptions>
      {/* 機器情報 */}
      <Descriptions
        bordered
        column={3}
        labelStyle={labelStyle}
        contentStyle={contentStyle}
        style={{ marginBottom: '20px', width: dataDispWidth.deviceDetail1350 }}
      >
        <Descriptions.Item label="機器分類" span={1}>
          {data?.sochi1Name ?? ''}
        </Descriptions.Item>
        <Descriptions.Item label="設置場所" span={1}>
          {data?.roomName ?? ''}
        </Descriptions.Item>
        <Descriptions.Item label="製造者名" span={1}>
          {data?.eqType ?? ''}
        </Descriptions.Item>
        <Descriptions.Item label="機器種別" span={1}>
          {data?.sochi2Name ?? ''}
        </Descriptions.Item>
        <Descriptions.Item label="形式" span={1}>
          {data?.eqType ?? ''}
        </Descriptions.Item>
        <Descriptions.Item label="製造番号" span={1}>
          {data?.seizoNo ?? ''}
        </Descriptions.Item>
        <Descriptions.Item label="その他分類" span={3}>
          {data?.sochi3Name ?? ''}
        </Descriptions.Item>
      </Descriptions>
      {/* 基本仕様項目 */}
      <Descriptions
        title="基本仕様項目"
        className={classes.descriptionTitle}
        bordered
        column={2}
        labelStyle={labelStyle}
        contentStyle={contentStyle}
        style={{ marginBottom: '20px', width: dataDispWidth.deviceDetail900 }}
      >
        <Descriptions.Item label="項目名称1" span={1}>
          {data?.baseItem1 ?? ''}
        </Descriptions.Item>
        <Descriptions.Item label="データ1" span={1}>
          {data?.baseData1 ?? ''}
        </Descriptions.Item>
        <Descriptions.Item label="項目名称2" span={1}>
          {data?.baseItem2 ?? ''}
        </Descriptions.Item>
        <Descriptions.Item label="データ2" span={1}>
          {data?.baseData2 ?? ''}
        </Descriptions.Item>
        <Descriptions.Item label="納入年月" span={1}>
          {`${data?.nonyuYear != null ? `${data?.nonyuYear}年` : ''} ${
            data?.nonyuMonth != null ? `${data?.nonyuMonth}月` : ''
          }`}
        </Descriptions.Item>
        <Descriptions.Item label="納入工号" span={1}>
          {data?.jobNo ?? ''}
        </Descriptions.Item>
        <Descriptions.Item label="使用場所" span={2}>
          {data?.setti ?? ''}
        </Descriptions.Item>
        <Descriptions.Item label="器具番号" span={2}>
          {data?.tagNo ?? ''}
        </Descriptions.Item>
        <Descriptions.Item label="台数" span={2}>
          {data?.qty ?? ''}
        </Descriptions.Item>
        <Descriptions.Item label="普通点検周期" span={2}>
          {data?.futuShuuki != null ? `${data?.futuShuuki}年` : ''}
        </Descriptions.Item>
        <Descriptions.Item label="精密点検周期" span={2}>
          {data?.saimituShuuki != null ? `${data?.saimituShuuki}年` : ''}
        </Descriptions.Item>
        <Descriptions.Item label="耐用年数" span={2}>
          {data?.lifeYear != null ? `${data?.lifeYear}年` : ''}
        </Descriptions.Item>
        <Descriptions.Item label="保全種別" span={2}>
          {data?.mainteType ?? ''}
        </Descriptions.Item>
      </Descriptions>
      {/* 仕様項目 */}
      <Descriptions
        title="仕様項目"
        className={classes.descriptionTitle}
        bordered
        column={1}
        labelStyle={labelStyle}
        contentStyle={contentStyle}
        style={{ marginBottom: '20px', width: dataDispWidth.deviceDetail450 }}
      >
        {numbers.map((num) => (
          <Descriptions.Item
            label={data ? data[`specItem${num}`] || blank : blank}
            span={1}
            contentStyle={contentStyle}
          >
            {data ? data[`specData${num}`] ?? '' : ''}
          </Descriptions.Item>
        ))}
      </Descriptions>
      {/* 故障影響度評価 */}
      <Descriptions
        title="故障影響度評価"
        className={classes.descriptionTitle}
        bordered
        column={1}
        labelStyle={labelStyle}
        contentStyle={contentStyle}
        style={{ marginBottom: '20px', width: dataDispWidth.deviceDetail450 }}
      >
        <Descriptions.Item label="安全面" span={1} contentStyle={contentStyle}>
          {data?.safety ?? ''}
        </Descriptions.Item>
        <Descriptions.Item label="環境面" span={1} contentStyle={contentStyle}>
          {data?.environment ?? ''}
        </Descriptions.Item>
        <Descriptions.Item
          label="設備重要度"
          span={1}
          contentStyle={contentStyle}
        >
          {data?.importance ?? ''}
        </Descriptions.Item>
        <Descriptions.Item label="機能面" span={1} contentStyle={contentStyle}>
          {data?.function ?? ''}
        </Descriptions.Item>
      </Descriptions>
      {/* 点検項目 */}
      <Descriptions
        title="点検項目"
        className={classes.descriptionTitle}
        bordered
        column={1}
        labelStyle={labelStyle}
        contentStyle={contentStyle}
        style={{ marginBottom: '20px', width: dataDispWidth.deviceDetail450 }}
      >
        <Descriptions.Item
          label="点検項目"
          span={1}
          contentStyle={{ ...contentStyle, ...contentHeight100 }}
        >
          {data?.inspectAll ?? ''}
        </Descriptions.Item>
      </Descriptions>
      {/* 備考・更新日 */}
      <Descriptions
        column={2}
        bordered
        labelStyle={labelStyle}
        contentStyle={contentStyle}
        style={{ marginBottom: '20px', width: dataDispWidth.deviceDetail900 }}
      >
        <Descriptions.Item label="備考" span={2}>
          {data?.biko ?? ''}
        </Descriptions.Item>
        <Descriptions.Item label="更新日" span={2}>
          {dateformat(data?.revData)}
        </Descriptions.Item>
      </Descriptions>
    </div>
  );
};

export default DataDisp;
